import React, { useEffect, useMemo, useState } from "react";
import { getKapasitetsOverstyringer } from "../../lib/tjenestekatalogApi";
import {
  normalizeWorkshopSlug,
  isoDate,
  weekStartISO,
  getWeekNumber,
  fmtMinutes,
  prettifyStoreName,
} from "./workshopCapacityUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ALL_STORES = ["sport1royken", "sport1slemmestad", "roykensykkelverksted"];

const MONTH_NAMES = [
  "Januar", "Februar", "Mars", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Desember",
];

function buildWeeksForMonth(year, month) {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const firstWeekStart = new Date(weekStartISO(firstDay));
  const weeks = [];
  let current = new Date(firstWeekStart);
  while (current <= lastDay) {
    weeks.push({
      weekIso: isoDate(current),
      weekNo: getWeekNumber(current),
      year: current.getFullYear(),
    });
    current = addDays(current, 7);
  }
  return weeks;
}

const DAGSMÅL_PER_BUTIKK = {
  sport1royken: 4000,
  sport1slemmestad: 1000,
  roykensykkelverksted: 0,
};
const ARBEIDSDAGER_PER_UKE = 5;

function getUkesmål(storeSlug) {
  if (storeSlug === "alle")
    return ALL_STORES.reduce((s, slug) => s + (DAGSMÅL_PER_BUTIKK[slug] || 0), 0) * ARBEIDSDAGER_PER_UKE;
  return (DAGSMÅL_PER_BUTIKK[storeSlug] ?? 0) * ARBEIDSDAGER_PER_UKE;
}

function getWeekOverrideForStore(overrides = [], storeSlug, weekIso) {
  const wanted = normalizeWorkshopSlug(storeSlug);
  return overrides.find((o) => {
    const loc = normalizeWorkshopSlug(o?.lokasjon || "");
    if (loc !== wanted) return false;
    if (!o?.uke) return false;
    return weekStartISO(o.uke) === weekIso;
  });
}

function getDateOverrideForStore(overrides = [], storeSlug, dateIso) {
  const wanted = normalizeWorkshopSlug(storeSlug);
  return overrides.find((o) => {
    const loc = normalizeWorkshopSlug(o?.lokasjon || "");
    if (loc !== wanted) return false;
    return o?.dato === dateIso;
  });
}

function isDateClosed(overrides, storeSlug, dateIso, weekIso) {
  const weekOv = getWeekOverrideForStore(overrides, storeSlug, weekIso);
  if (weekOv?.stengt) return true;
  const dateOv = getDateOverrideForStore(overrides, storeSlug, dateIso);
  return !!dateOv?.stengt;
}

function getMissedMålForWeek(overrides, storeSlug, weekIso, isAlle) {
  const weekStart = new Date(weekIso);
  let missedMål = 0;
  const slugs = isAlle ? ALL_STORES : [normalizeWorkshopSlug(storeSlug)];
  for (let i = 0; i < 5; i++) {
    const day = addDays(weekStart, i);
    const dayIso = isoDate(day);
    for (const slug of slugs) {
      if (isDateClosed(overrides, slug, dayIso, weekIso)) {
        missedMål += DAGSMÅL_PER_BUTIKK[slug] || 0;
      }
    }
  }
  return missedMål;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function buildPastWeekStarts(weeksBack) {
  const today = new Date();
  const currentWeekStart = new Date(weekStartISO(today));
  const weeks = [];
  for (let i = 0; i < weeksBack; i++) {
    const weekStart = addDays(currentWeekStart, -i * 7);
    weeks.push({
      weekIso: isoDate(weekStart),
      weekNo: getWeekNumber(weekStart),
      year: weekStart.getFullYear(),
    });
  }
  return weeks.reverse();
}

function fmtKr(amount) {
  return amount > 0 ? `${amount.toLocaleString("nb-NO")} kr` : "—";
}

function mergeHistoryRows(allRowArrays) {
  const byWeek = {};
  for (const rows of allRowArrays) {
    for (const row of rows) {
      if (!byWeek[row.weekIso]) {
        byWeek[row.weekIso] = { ...row };
      } else {
        const w = byWeek[row.weekIso];
        w.bruktMin = (w.bruktMin || 0) + (row.bruktMin || 0);
        w.omsetning = (w.omsetning || 0) + (row.omsetning || 0);
        w.dagOmsetning = (w.dagOmsetning || 0) + (row.dagOmsetning || 0);
        w.ukeOmsetning = (w.ukeOmsetning || 0) + (row.ukeOmsetning || 0);
        w.dropinOmsetning = (w.dropinOmsetning || 0) + (row.dropinOmsetning || 0);
        w.fortlopendeOmsetning = (w.fortlopendeOmsetning || 0) + (row.fortlopendeOmsetning || 0);
      }
    }
  }
  return Object.values(byWeek);
}

function WeekDetailModal({ open, onClose, title, services, showButikk }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4 bg-gray-50">
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            <div className="text-sm text-gray-500">{services.length} service(r)</div>
          </div>
          <button onClick={onClose} className="rounded border px-3 py-1.5 text-sm hover:bg-gray-100">
            Lukk
          </button>
        </div>
        <div className="overflow-auto max-h-[75vh] p-4">
          {services.length ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left">Ref</th>
                  <th className="px-3 py-2 text-left">Kunde</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  {showButikk && <th className="px-3 py-2 text-left">Butikk</th>}
                  <th className="px-3 py-2 text-left">Ferdigdato</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Servicelinjer</th>
                  <th className="px-3 py-2 text-right">Tid</th>
                  <th className="px-3 py-2 text-right">Omsetning</th>
                </tr>
              </thead>
              <tbody>
                {services.map((svc) => (
                  <tr key={svc.id} className="border-t align-top">
                    <td className="px-3 py-2 font-mono text-xs text-gray-600 whitespace-nowrap">{svc.serviceRef}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{svc.customerName}</td>
                    <td className="px-3 py-2 whitespace-nowrap capitalize">{svc.bookingtype || "–"}</td>
                    {showButikk && (
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                        {prettifyStoreName(svc.butikk)}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap">{svc.ferdigDato}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{svc.status}</td>
                    <td className="px-3 py-2">
                      {(svc.serviceLines || []).map((l, i) => (
                        <div key={i} className="text-xs text-gray-700">
                          {l.navn} — {fmtMinutes(l.tidsbruk)}
                          {l.pris > 0 && (
                            <span className="ml-2 text-gray-500">({l.pris.toLocaleString("nb-NO")} kr)</span>
                          )}
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">{fmtMinutes(svc.bruktMin)}</td>
                    <td className="px-3 py-2 text-right font-medium whitespace-nowrap">{fmtKr(svc.omsetning)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t bg-gray-50">
                <tr>
                  <td colSpan={showButikk ? 7 : 6} className="px-3 py-2 text-sm font-semibold text-right">
                    Total
                  </td>
                  <td className="px-3 py-2 text-right font-bold">
                    {fmtMinutes(services.reduce((s, r) => s + r.bruktMin, 0))}
                  </td>
                  <td className="px-3 py-2 text-right font-bold">
                    {fmtKr(services.reduce((s, r) => s + (r.omsetning || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="py-10 text-center text-gray-500">Ingen servicer funnet</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WorkshopOmsetningHistoryTab({ storeSlug }) {
  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  const [loading, setLoading] = useState(true);
  const [historyRows, setHistoryRows] = useState([]);
  const [details, setDetails] = useState([]);
  const [modal, setModal] = useState({ open: false, title: "", services: [] });
  const [overrides, setOverrides] = useState([]);
  const [filterMode, setFilterMode] = useState("siste4");
  const [filterMonth, setFilterMonth] = useState(currentMonth);
  const [filterYear, setFilterYear] = useState(currentYear);

  const isAlle = storeSlug === "alle";

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= 2023; y--) years.push(y);
    return years;
  }, [currentYear]);

  const { fromISO, toISO, weeksList } = useMemo(() => {
    if (filterMode === "siste4") {
      const currentWeekStart = new Date(weekStartISO(today));
      const from = addDays(currentWeekStart, -3 * 7);
      return {
        fromISO: isoDate(from),
        toISO: isoDate(today),
        weeksList: buildPastWeekStarts(4),
      };
    }
    const firstDay = new Date(filterYear, filterMonth - 1, 1);
    const lastDay = new Date(filterYear, filterMonth, 0);
    const firstWeekStart = new Date(weekStartISO(firstDay));
    const cappedTo = lastDay > today ? today : lastDay;
    return {
      fromISO: isoDate(firstWeekStart),
      toISO: isoDate(cappedTo),
      weeksList: buildWeeksForMonth(filterYear, filterMonth),
    };
  }, [filterMode, filterMonth, filterYear, today]);

  useEffect(() => {
    getKapasitetsOverstyringer()
      .then((result) => setOverrides(result || []))
      .catch(() => setOverrides([]));
  }, []);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const slugs = isAlle ? ALL_STORES : [normalizeWorkshopSlug(storeSlug)];

        const responses = await Promise.all(
          slugs.map((slug) =>
            fetch(
              `${API_BASE_URL}/workshop/capacity-history?verkstedSlug=${encodeURIComponent(slug)}&from=${fromISO}&to=${toISO}`
            ).then((r) => (r.ok ? r.json() : { rows: [], details: [] }))
          )
        );

        const mergedRows = mergeHistoryRows(responses.map((r) => r.rows || []));
        const mergedDetails = responses.flatMap((r) =>
          (r.details || []).filter((d) => !d.reason)
        );

        setHistoryRows(mergedRows);
        setDetails(mergedDetails);
      } catch (err) {
        console.error("Feil ved henting av omsetningshistorikk:", err);
        setHistoryRows([]);
        setDetails([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [storeSlug, fromISO, toISO, isAlle]);

  const malPerUke = getUkesmål(storeSlug);

  const rows = useMemo(() => {
    return weeksList.map((w) => {
      const r = (historyRows || []).find((h) => String(h.weekIso) === String(w.weekIso)) || {};
      const omsetning = Number(r.omsetning || 0);
      const missedMål = getMissedMålForWeek(overrides, storeSlug, w.weekIso, isAlle);
      const adjustedMål = Math.max(0, malPerUke - missedMål);
      return {
        ...w,
        bruktTotalt: Number(r.bruktMin || 0),
        omsetning,
        dagOmsetning: Number(r.dagOmsetning || 0),
        ukeOmsetning: Number(r.ukeOmsetning || 0),
        dropinOmsetning: Number(r.dropinOmsetning || 0),
        fortlopendeOmsetning: Number(r.fortlopendeOmsetning || 0),
        missedMål,
        adjustedMål,
        differanse: omsetning - adjustedMål,
        pctOfMal: adjustedMål > 0 ? Math.round((omsetning / adjustedMål) * 100) : 0,
      };
    });
  }, [historyRows, weeksList, malPerUke, overrides, storeSlug, isAlle]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.brukt += row.bruktTotalt;
        acc.omsetning += row.omsetning;
        acc.dag += row.dagOmsetning;
        acc.uke += row.ukeOmsetning;
        acc.dropin += row.dropinOmsetning;
        acc.fortlopende += row.fortlopendeOmsetning;
        if (row.omsetning > 0) acc.aktiveUker += 1;
        return acc;
      },
      { brukt: 0, omsetning: 0, dag: 0, uke: 0, dropin: 0, fortlopende: 0, aktiveUker: 0 }
    );
  }, [rows]);

  const totalMissedHistory = useMemo(
    () => rows.reduce((s, r) => s + (r.missedMål || 0), 0),
    [rows]
  );

  const dagsmål = isAlle
    ? ALL_STORES.reduce((s, slug) => s + (DAGSMÅL_PER_BUTIKK[slug] || 0), 0)
    : (DAGSMÅL_PER_BUTIKK[storeSlug] || 0);

  const snitDagsomsetning =
    totals.aktiveUker > 0
      ? Math.round(totals.omsetning / (totals.aktiveUker * ARBEIDSDAGER_PER_UKE))
      : 0;

  const dagsPct = dagsmål > 0 ? Math.min(Math.round((snitDagsomsetning / dagsmål) * 100), 200) : 0;
  const dagsDiff = snitDagsomsetning - dagsmål;

  function openModal(row) {
    const services = details.filter((d) => d.ukeISO === row.weekIso);
    setModal({
      open: true,
      title: `Uke ${row.weekNo} / ${row.year} — ${fmtKr(row.omsetning)}`,
      services,
    });
  }

  if (loading) {
    return <div className="py-10 text-center">Laster omsetningshistorikk…</div>;
  }

  return (
    <div className="space-y-6">
      <WeekDetailModal
        open={modal.open}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        title={modal.title}
        services={modal.services}
        showButikk={isAlle}
      />

      <div className="rounded border bg-green-50 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm text-gray-600">Historisk omsetning for</div>
            <div className="text-xl font-bold">
              {isAlle ? "Alle verksteder" : prettifyStoreName(storeSlug)}
            </div>
            <div className="text-sm text-gray-700 mt-1">
              Viser tidligere uker basert på ferdigdato. Ukesmål: {malPerUke.toLocaleString("nb-NO")} kr
              {isAlle
                ? ` (${ALL_STORES.filter((s) => DAGSMÅL_PER_BUTIKK[s] > 0).map((s) => `${prettifyStoreName(s)}: ${(DAGSMÅL_PER_BUTIKK[s] * ARBEIDSDAGER_PER_UKE).toLocaleString("nb-NO")} kr`).join(", ")})`
                : ` (${(DAGSMÅL_PER_BUTIKK[storeSlug] || 0).toLocaleString("nb-NO")} kr/dag × ${ARBEIDSDAGER_PER_UKE} dager)`}.
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilterMode("siste4")}
              className={`px-3 py-1.5 rounded border text-sm ${
                filterMode === "siste4"
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Siste 4 uker
            </button>
            <button
              onClick={() => setFilterMode("maned")}
              className={`px-3 py-1.5 rounded border text-sm ${
                filterMode === "maned"
                  ? "bg-green-600 text-white border-green-600"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Måned
            </button>
            {filterMode === "maned" && (
              <>
                <select
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(Number(e.target.value))}
                  className="border rounded px-2 py-1.5 text-sm bg-white"
                >
                  {MONTH_NAMES.map((name, i) => (
                    <option key={i + 1} value={i + 1}>{name}</option>
                  ))}
                </select>
                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(Number(e.target.value))}
                  className="border rounded px-2 py-1.5 text-sm bg-white"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-5 gap-4">
        <div className="rounded border p-4 bg-amber-50">
          <div className="text-sm text-gray-600">Tid totalt</div>
          <div className="text-xl font-bold">{fmtMinutes(totals.brukt)}</div>
        </div>
        <div className="rounded border p-4 bg-green-50">
          <div className="text-sm text-gray-600">Omsetning totalt</div>
          <div className="text-xl font-bold">{fmtKr(totals.omsetning)}</div>
          {malPerUke > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Mål: {(rows.length * malPerUke).toLocaleString("nb-NO")} kr
            </div>
          )}
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Dag</div>
          <div className="text-xl font-bold">{fmtKr(totals.dag)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Uke</div>
          <div className="text-xl font-bold">{fmtKr(totals.uke)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Drop-in</div>
          <div className="text-xl font-bold">{fmtKr(totals.dropin)}</div>
        </div>
        <div className="rounded border p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Fortløpende</div>
          <div className="text-xl font-bold">{fmtKr(totals.fortlopende)}</div>
        </div>
      </div>

      {dagsmål > 0 && totals.aktiveUker > 0 && (
        <div className="rounded border bg-white p-5">
          <div className="text-sm font-semibold text-gray-700 mb-4">
            Snitt dagsomsetning vs dagsmål
            <span className="ml-2 text-xs font-normal text-gray-500">
              (basert på {totals.aktiveUker} uke{totals.aktiveUker !== 1 ? "r" : ""} med data)
            </span>
          </div>

          <div className="flex items-center gap-6">
            <div className="text-center min-w-[110px]">
              <div className="text-2xl font-bold text-gray-900">
                {snitDagsomsetning.toLocaleString("nb-NO")} kr
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Snitt per dag</div>
            </div>

            <div className="flex-1">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>0</span>
                <span>Mål: {dagsmål.toLocaleString("nb-NO")} kr</span>
              </div>
              <div className="relative h-5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    dagsPct >= 100
                      ? "bg-green-500"
                      : dagsPct >= 70
                      ? "bg-amber-400"
                      : "bg-red-400"
                  }`}
                  style={{ width: `${Math.min(dagsPct, 100)}%` }}
                />
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-gray-400"
                  style={{ left: "100%" }}
                />
              </div>
              <div className="mt-1 text-xs text-center font-medium text-gray-600">
                {dagsPct} % av dagsmål
              </div>
            </div>

            <div className="text-center min-w-[110px]">
              <div
                className={`text-2xl font-bold ${
                  dagsDiff >= 0 ? "text-green-600" : "text-red-500"
                }`}
              >
                {dagsDiff >= 0 ? "+" : ""}
                {dagsDiff.toLocaleString("nb-NO")} kr
              </div>
              <div className="text-xs text-gray-500 mt-0.5">Differanse per dag</div>
            </div>
          </div>

          {isAlle && (
            <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4">
              {ALL_STORES.filter((s) => DAGSMÅL_PER_BUTIKK[s] > 0).map((slug) => {
                const slugDetails = details.filter((d) => d.butikk === slug);
                const ukerMedData = new Set(slugDetails.map((d) => d.ukeISO)).size;
                const totalOms = slugDetails.reduce((s, d) => s + (d.omsetning || 0), 0);
                const snitt = ukerMedData > 0 ? Math.round(totalOms / (ukerMedData * ARBEIDSDAGER_PER_UKE)) : 0;
                const mål = DAGSMÅL_PER_BUTIKK[slug];
                const pct = mål > 0 ? Math.min(Math.round((snitt / mål) * 100), 200) : 0;
                return (
                  <div key={slug} className="text-sm">
                    <div className="font-medium text-gray-700 mb-1">{prettifyStoreName(slug)}</div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden mb-1">
                      <div
                        className={`h-full rounded-full ${pct >= 100 ? "bg-green-500" : pct >= 70 ? "bg-amber-400" : "bg-red-400"}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-600">
                      {snitt.toLocaleString("nb-NO")} kr/dag
                      <span className="mx-1 text-gray-400">vs</span>
                      {mål.toLocaleString("nb-NO")} kr ({pct} %)
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {totalMissedHistory > 0 && dagsmål > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 flex items-center gap-3">
          <span className="text-amber-500 shrink-0">⚠</span>
          <div className="text-sm text-amber-800">
            Stengte dager har redusert omsetningsmålet med totalt{" "}
            <strong>{totalMissedHistory.toLocaleString("nb-NO")} kr</strong>{" "}
            over {weeksList.length} uke{weeksList.length !== 1 ? "r" : ""}. Differanse og mål-% er beregnet mot justert mål (uten stengte dager).
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full border rounded bg-white">
          <thead className="bg-gray-100 text-sm text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Uke</th>
              <th className="px-4 py-2 text-left">Uke start</th>
              <th className="px-4 py-2 text-right">Totalt tid</th>
              <th className="px-4 py-2 text-right">Omsetning</th>
              <th className="px-4 py-2 text-right">Dag</th>
              <th className="px-4 py-2 text-right">Uke</th>
              <th className="px-4 py-2 text-right">Drop-in</th>
              <th className="px-4 py-2 text-right">Fortløpende</th>
              <th className="px-4 py-2 text-right">Mål</th>
              <th className="px-4 py-2 text-right">Differanse</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length ? (
              rows.map((row) => (
                <tr key={row.weekIso} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">
                    <div className="flex items-center gap-2 flex-wrap">
                      Uke {row.weekNo} / {row.year}
                      {row.missedMål > 0 && (
                        <span
                          className="text-xs text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded"
                          title={`Stengte dager reduserte ukesmålet med ${row.missedMål.toLocaleString("nb-NO")} kr`}
                        >
                          −{row.missedMål.toLocaleString("nb-NO")} kr mål
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-gray-500">{row.weekIso}</td>
                  <td className="px-4 py-2 text-right">{fmtMinutes(row.bruktTotalt)}</td>
                  <td className="px-4 py-2 text-right font-medium">{fmtKr(row.omsetning)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{fmtKr(row.dagOmsetning)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{fmtKr(row.ukeOmsetning)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{fmtKr(row.dropinOmsetning)}</td>
                  <td className="px-4 py-2 text-right text-gray-600">{fmtKr(row.fortlopendeOmsetning)}</td>
                  <td className="px-4 py-2 text-right">
                    {malPerUke > 0 && row.omsetning > 0 ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span
                          className={`px-2 py-1 rounded text-sm font-medium ${
                            row.pctOfMal >= 100
                              ? "bg-green-100 text-green-700"
                              : row.pctOfMal >= 70
                              ? "bg-amber-100 text-amber-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {row.pctOfMal} %
                        </span>
                        {row.missedMål > 0 && (
                          <span className="text-xs text-gray-400">justert mål</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {malPerUke > 0 && row.omsetning > 0 ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className={row.differanse >= 0 ? "text-green-700 font-medium" : "text-red-600 font-medium"}>
                          {row.differanse >= 0 ? "+" : ""}
                          {row.differanse.toLocaleString("nb-NO")} kr
                        </span>
                        {row.missedMål > 0 && (
                          <span className="text-xs text-gray-400">vs justert mål</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {(row.bruktTotalt > 0 || row.omsetning > 0) && (
                      <button
                        onClick={() => openModal(row)}
                        className="rounded border px-3 py-1 text-xs hover:bg-gray-100"
                      >
                        Se detaljer
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={11} className="px-4 py-6 text-center text-gray-500">
                  Ingen historikk funnet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
